import Image from 'next/image'
import clsx from 'clsx'

import Link from '@/atoms/Link/Link'

/* utils */
import { ResourceDropdownProps, CategoriesDropdownProps } from './Header.utils'

/* assets */
import { ReactComponent as ArrowLeft } from 'public/assets/icons/chevron-right.svg'
import { ReactComponent as ForwardArrow } from 'public/assets/cmp/navigation/icons/forward-arrow.svg'

export function ResourcesDropdown({
  library,
  resources,
  description,
  image
}: ResourceDropdownProps): JSX.Element {
  return (
    <section
      className={clsx(
        'max-header-width | grid grid-cols-4 lg:grid-cols-12 | gap-4 lg:gap-8 | lg:pl-22 mx-auto | bg-white'
      )}
    >
      <div className="col-span-4 | px-2 lg:px-6 pt-4 lg:pt-10 lg:pb-14">
        <p className="font-semibold text-small text-gray-50 uppercase | mb-2 lg:mb-4 px-6">
          Library
        </p>
        {library.map(item => (
          <Link
            key={item.slug}
            href={item.slug}
            target={item.external ? '_blank' : '_self'}
            rel={item.external ? 'noopener noreferrer' : ''}
            className="rounded-xl | flex items-center | p-4 | group transition-colors | hover:text-blue-50 hover:bg-gray-20 focus:outline-none focus:bg-gray-20"
          >
            <Image
              src={item.icon}
              alt="icon"
              width="32"
              height="32"
              className="w-8 h-8"
              loading="lazy"
            />
            <div className="ml-6 ">
              <div className="font-medium text-lg text-gray-90 | flex items-center | mb-1 | group-hover:text-blue-50">
                {item.title}
                <span>
                  <ForwardArrow className="ml-2 | font-semibold | group-hover:translate-x-1 transition duration-150 ease-linear" />
                </span>
              </div>
              <p className="text-xs text-gray-60">{item.description}</p>
            </div>
          </Link>
        ))}
      </div>
      <div className="col-span-4 | px-2 lg:px-6 lg:pt-10 lg:pb-14">
        <p className="font-semibold text-small text-gray-50 uppercase | mb-2 lg:mb-4 px-6">
          Resources
        </p>
        {resources.map(item => (
          <Link
            key={item.slug}
            href={item.slug}
            target={item.external ? '_blank' : '_self'}
            rel={item.external ? 'noopener noreferrer' : ''}
            className="rounded-xl | flex items-center | p-4 | group transition-colors | hover:text-blue-50 hover:bg-gray-20 focus:outline-none focus:bg-gray-20"
          >
            <Image
              src={item.icon}
              alt="icon"
              width="32"
              height="32"
              className="w-8 h-8"
              loading="lazy"
            />
            <div className="ml-6 ">
              <div className="font-medium text-lg text-gray-90 | flex items-center | mb-1 | group-hover:text-blue-50">
                {item.title}
                <span>
                  <ForwardArrow className="ml-2 | font-semibold | group-hover:translate-x-1 transition duration-150 ease-linear" />
                </span>
              </div>
              <p className="text-xs text-gray-60">{item.description}</p>
            </div>
          </Link>
        ))}
      </div>

      <div className="col-span-4 text-gray-90 | lg:pt-10 lg:pb-14 lg:pr-22 lg:pl-8 px-6 py-8 | bg-gray-10">
        <p className="font-semibold text-small text-gray-50 uppercase | mb-8">
          Updates
        </p>
        <Image
          src={image.url}
          alt={image.alternativeText ?? ''}
          height={image.height}
          width={image.width}
          className="mb-5"
        />
        <p className="text-lg | mb-5">{description}</p>
        <p className="font-medium text-xs text-blue-50 | flex items-center | group hover:underline decoration-2 underline-offset-4">
          <Link href="/global-marketing-leaders/">Learn More</Link>
          <ArrowLeft className="ml-3 | group-hover:translate-x-1 transition duration-150 ease-linear" />
        </p>
      </div>
    </section>
  )
}

export function CategoriesDropdown({
  links
}: CategoriesDropdownProps): JSX.Element {
  const parts = Math.ceil(links.length / 3) // Calculate the size of each part

  const firstPart = links.slice(0, parts) // First part
  const secondPart = links.slice(parts, parts * 2) // Second part
  const thirdPart = links.slice(parts * 2, links.length) // Third part
  return (
    <section className="max-header-width | lg:pl-22 mx-auto | bg-white">
      <div className="px-2 lg:px-6 pt-4 lg:pt-10 lg:pb-14">
        <p className="font-semibold text-small text-gray-50 uppercase | mb-4 px-6">
          Categories
        </p>

        <div className="grid lg:grid-cols-12 gap-4 lg:gap-8">
          <div className="lg:col-span-4 | lg:pr-8 px-2 lg:px-6 | order-2 lg:order-1">
            {links &&
              firstPart.map(item => (
                <Link
                  key={item.slug}
                  href={item.slug}
                  className="rounded-xl | flex items-center | p-4 | group transition-colors | hover:text-blue-50 hover:bg-gray-20 focus:outline-none focus:bg-gray-20"
                >
                  <Image
                    src={item.icon}
                    alt="icon"
                    width="32"
                    height="32"
                    className="w-8 h-8"
                    loading="lazy"
                  />
                  <div className="ml-6 ">
                    <div className="font-medium text-base lg:text-lg text-gray-90  flex items-center | mb-1 | group-hover:text-blue-50">
                      {item.title}
                      <span>
                        <ForwardArrow className="ml-2 | font-semibold | group-hover:translate-x-1 transition duration-150 ease-linear" />{' '}
                      </span>
                    </div>
                    <p className="text-xs text-gray-60">{item.description}</p>
                  </div>
                </Link>
              ))}
          </div>
          <div className="lg:col-span-4 | lg:pr-8 px-2 lg:px-6 | order-1 lg:order-2">
            {links &&
              secondPart.map(item => (
                <Link
                  key={item.slug}
                  href={item.slug}
                  className="rounded-xl | flex items-center | p-4 | group transition-colors | hover:text-blue-50 hover:bg-gray-20 focus:outline-none focus:bg-gray-20"
                >
                  <Image
                    src={item.icon}
                    alt="icon"
                    width="32"
                    height="32"
                    className="w-8 h-8"
                    loading="lazy"
                  />
                  <div className="ml-6 ">
                    <div className="font-medium text-lg text-gray-90 | flex items-center | mb-1 | group-hover:text-blue-50">
                      {item.title}
                      <span>
                        <ForwardArrow className="ml-2 | font-semibold | group-hover:translate-x-1 transition duration-150 ease-linear" />{' '}
                      </span>
                    </div>
                    <p className="text-xs text-gray-60">{item.description}</p>
                  </div>
                </Link>
              ))}
          </div>
          <div className="lg:col-span-4 | lg:pr-8 px-2 lg:px-6 | order-1 lg:order-2">
            {links &&
              thirdPart.map(item => (
                <Link
                  key={item.slug}
                  href={item.slug}
                  className="rounded-xl | flex items-center | p-4 | group transition-colors | hover:text-blue-50 hover:bg-gray-20 focus:outline-none focus:bg-gray-20"
                >
                  <Image
                    src={item.icon}
                    alt="icon"
                    width="32"
                    height="32"
                    className="w-8 h-8"
                    loading="lazy"
                  />
                  <div className="ml-6 ">
                    <div className="font-medium text-lg text-gray-90 | flex items-center | mb-1 | group-hover:text-blue-50">
                      {item.title}
                      <span>
                        <ForwardArrow className="ml-2 | font-semibold | group-hover:translate-x-1 transition duration-150 ease-linear" />{' '}
                      </span>
                    </div>
                    <p className="text-xs text-gray-60">{item.description}</p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </section>
  )
}
